/* eslint-disable complexity, react-hooks/exhaustive-deps */
import { useEffect, useRef, useState } from "react";

import FocusTrap from "focus-trap-react";

import {
    AnalyticsAttributesProvider,
    ChevronLeft,
    ChevronRight,
    LinkButton,
    Markdown,
    useAnalytics
} from "../../../components";
import { UiHeaderNavModel } from "../../../models";
import UiNavBanner from "../../UiNavBanner/UiNavBanner";
import UiNavBlogArticle from "../../UiNavBlogArticle/UiNavBlogArticle";
import UiNavLink from "../../UiNavLink/UiNavLink";
import { Logo, HeaderTabLink, ProgressBar } from "../shared";

import MenuIcon from "./MenuIcon";
import {
    BackButton,
    Button,
    ButtonDark,
    ButtonPrimary,
    Buttons,
    Header,
    Link,
    LogoWrapper,
    MenuButton,
    NavBar,
    NavLinkContainer,
    Page,
    PageContainer,
    Secondary,
    SecondaryCtaContainer,
    Tab,
    TabList
} from "./SmallNav.styles";

type SmallNavProps = {
    hasScrolled: boolean;
    isLoggedIn?: boolean;
    signUpUrl: string;
    loginUrl: string;
    model: UiHeaderNavModel;
};

const SmallNav = ({
    hasScrolled,
    isLoggedIn,
    signUpUrl,
    loginUrl,
    model: {
        id,
        content: {
            goBackText,
            goToBoardsText,
            logInText,
            navigationLabel,
            progressBar,
            progressLabel,
            signUpText,
            startTransparent,
            tabs,
            useEyebrowLogo
        }
    }
}: SmallNavProps) => {
    const [isOpen, setIsOpen] = useState(false);
    const [activeTab, setActiveTab] = useState<number | undefined>();
    const { sendTrackEvent } = useAnalytics();
    const analyticsAttributes = { eventContainer: "mobileNav" };
    const headerRef = useRef<HTMLDivElement>(null);

    function close() {
        setIsOpen(false);
        setTimeout(() => setActiveTab(undefined), 300);
    }

    function escPress(event: KeyboardEvent) {
        if (event.key === "Escape") {
            close();
        }
    }

    function open() {
        sendTrackEvent("clickedMobileNavMenu", {
            ...analyticsAttributes,
            event: "clicked",
            eventComponent: "mobileNavMenu",
            label: "menu open",
            labelEnglish: "menu open",
            uuid: `${id}_mobileMenu`
        });
        setIsOpen(true);
    }

    useEffect(() => {
        if (isOpen) {
            window.addEventListener("keydown", escPress);
            document.body.classList.add("prevent-scroll");
        }
        return () => {
            window.removeEventListener("keydown", escPress);
            document.body.classList.remove("prevent-scroll");
        };
    }, [isOpen]);

    return (
        <AnalyticsAttributesProvider value={analyticsAttributes}>
            <FocusTrap
                active={isOpen}
                focusTrapOptions={{
                    fallbackFocus: "#skip-to-content"
                }}
            >
                <Header
                    data-scrolled={String(!isOpen && hasScrolled)}
                    data-open={String(isOpen)}
                    data-testid="smallnav"
                    startTransparent={startTransparent}
                >
                    <NavBar ref={headerRef}>
                        <LogoWrapper data-hidden={activeTab !== undefined}>
                            <Logo
                                id={`${id}_mobile`}
                                eyebrow={useEyebrowLogo}
                            />
                        </LogoWrapper>
                        <BackButton
                            data-hidden={activeTab === undefined || !isOpen}
                            onClick={() => {
                                sendTrackEvent("clickedMobileNavBackButton", {
                                    event: "clicked",
                                    eventComponent: "mobileNavBackButton",
                                    eventContainer: "mobileNav",
                                    label: goBackText,
                                    labelEnglish: "Back",
                                    uuid: `${id}_back`
                                });
                                setActiveTab(undefined);
                            }}
                            tabIndex={
                                activeTab === undefined || !isOpen
                                    ? -1
                                    : undefined
                            }
                        >
                            <ChevronLeft />
                            {goBackText}
                        </BackButton>
                        <MenuButton
                            aria-expanded={isOpen}
                            aria-label={navigationLabel}
                            onClick={() => (isOpen ? close() : open())}
                            data-testid="menubutton"
                        >
                            <MenuIcon isOpen={isOpen} />
                            <span hidden>{navigationLabel}</span>
                        </MenuButton>
                        {progressBar && (
                            <ProgressBar progressLabel={progressLabel} />
                        )}
                    </NavBar>
                    <PageContainer open={isOpen}>
                        <Page
                            aria-hidden={!isOpen || activeTab !== undefined}
                            data-offscreen={
                                activeTab !== undefined ? "left" : undefined
                            }
                        >
                            <TabList>
                                {tabs?.map((tab, index) =>
                                    tab.type === "uiHeaderNavTab" ? (
                                        <Tab
                                            key={index}
                                            onClick={() => {
                                                sendTrackEvent(
                                                    "clickedMobileNavTab",
                                                    {
                                                        event: "clicked",
                                                        eventComponent:
                                                            "mobileNavTab",
                                                        eventContainer:
                                                            "mobileNav",
                                                        label: tab.content
                                                            .title,
                                                        labelEnglish:
                                                            tab.content
                                                                .entryTitle,
                                                        uuid: tab.id
                                                    }
                                                );
                                                setActiveTab(index);
                                            }}
                                            tabIndex={
                                                !isOpen ||
                                                activeTab !== undefined
                                                    ? -1
                                                    : undefined
                                            }
                                        >
                                            {tab.content.title}
                                            <ChevronRight />
                                        </Tab>
                                    ) : tab.type === "uiLink" ? (
                                        <Link
                                            key={index}
                                            href={tab.content.url}
                                            onClick={() =>
                                                sendTrackEvent(
                                                    "clickedMobileNavLink",
                                                    {
                                                        event: "clicked",
                                                        eventComponent:
                                                            "mobileNavLink",
                                                        eventContainer:
                                                            "mobileNav",
                                                        href: tab.content.url,
                                                        label: tab.content.text,
                                                        labelEnglish:
                                                            tab.content
                                                                .entryTitle,
                                                        uuid: tab.id
                                                    }
                                                )
                                            }
                                            tabIndex={
                                                !isOpen ||
                                                activeTab !== undefined
                                                    ? -1
                                                    : undefined
                                            }
                                        >
                                            {tab.content.text}
                                        </Link>
                                    ) : null
                                )}
                            </TabList>
                            <Buttons>
                                {isLoggedIn === true && goToBoardsText && (
                                    <ButtonDark
                                        href="/"
                                        onClick={() =>
                                            sendTrackEvent(
                                                "clickedMobileNavButton",
                                                {
                                                    event: "clicked",
                                                    eventComponent:
                                                        "mobileNavButton",
                                                    eventContainer: "mobileNav",
                                                    href: "/",
                                                    label: goToBoardsText,
                                                    labelEnglish:
                                                        "Go to your boards",
                                                    uuid: `${id}_goToBoards`
                                                }
                                            )
                                        }
                                        tabIndex={
                                            !isOpen || activeTab !== undefined
                                                ? -1
                                                : undefined
                                        }
                                    >
                                        {goToBoardsText}
                                    </ButtonDark>
                                )}
                                {(isLoggedIn === false ||
                                    isLoggedIn === undefined) &&
                                    logInText &&
                                    signUpText && (
                                        <>
                                            <ButtonPrimary
                                                href={signUpUrl}
                                                onClick={() =>
                                                    sendTrackEvent(
                                                        "clickedMobileNavButton",
                                                        {
                                                            event: "clicked",
                                                            eventComponent:
                                                                "mobileNavButton",
                                                            eventContainer:
                                                                "mobileNav",
                                                            href: signUpUrl,
                                                            label: signUpText,
                                                            labelEnglish:
                                                                "Get Trello for free",
                                                            uuid: `${id}_signup`
                                                        }
                                                    )
                                                }
                                                tabIndex={
                                                    !isOpen ||
                                                    activeTab !== undefined
                                                        ? -1
                                                        : undefined
                                                }
                                            >
                                                {signUpText}
                                            </ButtonPrimary>
                                            <Button
                                                href={loginUrl}
                                                onClick={() =>
                                                    sendTrackEvent(
                                                        "clickedMobileNavButton",
                                                        {
                                                            event: "clicked",
                                                            eventComponent:
                                                                "mobileNavButton",
                                                            eventContainer:
                                                                "mobileNav",
                                                            href: "/login",
                                                            label: logInText,
                                                            labelEnglish:
                                                                "Log in",
                                                            uuid: `${id}_logIn`
                                                        }
                                                    )
                                                }
                                                tabIndex={
                                                    !isOpen ||
                                                    activeTab !== undefined
                                                        ? -1
                                                        : undefined
                                                }
                                            >
                                                {logInText}
                                            </Button>
                                        </>
                                    )}
                            </Buttons>{" "}
                            {/* end auth buttons */}{" "}
                        </Page>
                        {tabs?.map((tab, index) =>
                            tab.type === "uiHeaderNavTab" ? (
                                <Page
                                    key={index}
                                    aria-hidden={!isOpen || activeTab !== index}
                                    data-offscreen={
                                        activeTab !== index
                                            ? "right"
                                            : undefined
                                    }
                                >
                                    <AnalyticsAttributesProvider
                                        value={{
                                            ...analyticsAttributes,
                                            parentLabel: tab.content.title,
                                            labelEnglish: tab.content.entryTitle
                                        }}
                                    >
                                        <TabList>
                                            {tab.content.primaryTitle && (
                                                <h2>
                                                    {tab.content.primaryTitle}
                                                </h2>
                                            )}
                                            {tab.content.primaryLinks &&
                                                tab.content.primaryLinks
                                                    .filter(
                                                        ({ type }) =>
                                                            type === "uiNavLink"
                                                    )
                                                    .map((link, linkIndex) => (
                                                        <NavLinkContainer
                                                            key={linkIndex}
                                                        >
                                                            <UiNavLink
                                                                model={link}
                                                                tabIndex={
                                                                    activeTab !==
                                                                    index
                                                                        ? -1
                                                                        : undefined
                                                                }
                                                            />
                                                        </NavLinkContainer>
                                                    ))}
                                            {tab.content.primaryCta
                                                ?.content && (
                                                <HeaderTabLink
                                                    url={
                                                        tab.content.primaryCta
                                                            .content.url
                                                    }
                                                    text={
                                                        tab.content.primaryCta
                                                            .content.text
                                                    }
                                                    ariaLabel={
                                                        tab.content.primaryCta
                                                            .content.ariaLabel
                                                    }
                                                    onClick={() =>
                                                        sendTrackEvent(
                                                            "clickedMobileNavPrimaryLink",
                                                            {
                                                                ...analyticsAttributes,
                                                                event: "clicked",
                                                                eventComponent:
                                                                    "mobileNavPrimaryLink",
                                                                href: tab
                                                                    .content
                                                                    .primaryCta
                                                                    ?.content
                                                                    .url,
                                                                label: tab
                                                                    .content
                                                                    .primaryCta
                                                                    ?.content
                                                                    .text,
                                                                labelEnglish:
                                                                    tab.content
                                                                        .primaryCta
                                                                        ?.content
                                                                        .entryTitle,
                                                                parentLabel:
                                                                    tab.content
                                                                        .title,
                                                                uuid: tab
                                                                    .content
                                                                    .primaryCta
                                                                    ?.id
                                                            }
                                                        )
                                                    }
                                                    tabIndex={
                                                        activeTab !== index
                                                            ? -1
                                                            : undefined
                                                    }
                                                />
                                            )}
                                        </TabList>
                                        {tab.content.primaryBanner &&
                                            tab.content.primaryBanner.type ===
                                                "uiNavBanner" && (
                                                <AnalyticsAttributesProvider
                                                    value={{
                                                        eventComponent:
                                                            "globalNavPrimaryButton"
                                                    }}
                                                >
                                                    <UiNavBanner
                                                        model={
                                                            tab.content
                                                                .primaryBanner
                                                        }
                                                        tabIndex={
                                                            activeTab !== index
                                                                ? -1
                                                                : undefined
                                                        }
                                                    />
                                                </AnalyticsAttributesProvider>
                                            )}
                                        {(tab.content.secondaryTitle ||
                                            tab.content.secondaryBlogArticle
                                                ?.content) && (
                                            <Secondary>
                                                {tab.content
                                                    .secondaryBlogArticle
                                                    ?.content ? (
                                                    <UiNavBlogArticle
                                                        model={
                                                            tab.content
                                                                .secondaryBlogArticle
                                                        }
                                                        tabIndex={
                                                            activeTab !== index
                                                                ? -1
                                                                : undefined
                                                        }
                                                        isShowing={
                                                            activeTab === index
                                                        }
                                                    />
                                                ) : (
                                                    <>
                                                        <h3>
                                                            {
                                                                tab.content
                                                                    .secondaryTitle
                                                            }
                                                        </h3>
                                                        {tab.content
                                                            .secondaryDescription && (
                                                            <Markdown>
                                                                {
                                                                    tab.content
                                                                        .secondaryDescription
                                                                }
                                                            </Markdown>
                                                        )}
                                                        <SecondaryCtaContainer>
                                                            {/* this UiLink needs to look like a button on mobile */}
                                                            {tab.content
                                                                .secondaryCta
                                                                ?.content && (
                                                                <LinkButton
                                                                    url={
                                                                        tab
                                                                            .content
                                                                            .secondaryCta
                                                                            .content
                                                                            .url
                                                                    }
                                                                    aria-label={
                                                                        tab
                                                                            .content
                                                                            .secondaryCta
                                                                            .content
                                                                            .ariaLabel
                                                                    }
                                                                    onClick={() =>
                                                                        sendTrackEvent(
                                                                            "clickedMobileNavSecondaryLink",
                                                                            {
                                                                                ...analyticsAttributes,
                                                                                event: "clicked",
                                                                                eventComponent:
                                                                                    "mobileNavSecondaryLink",
                                                                                href: tab
                                                                                    .content
                                                                                    .secondaryCta
                                                                                    ?.content
                                                                                    .url,
                                                                                label: tab
                                                                                    .content
                                                                                    .secondaryCta
                                                                                    ?.content
                                                                                    .text,
                                                                                labelEnglish:
                                                                                    tab
                                                                                        .content
                                                                                        .secondaryCta
                                                                                        ?.content
                                                                                        .entryTitle,
                                                                                parentLabel:
                                                                                    tab
                                                                                        .content
                                                                                        .title,
                                                                                uuid: tab
                                                                                    .content
                                                                                    .secondaryCta
                                                                                    ?.id
                                                                            }
                                                                        )
                                                                    }
                                                                    tabIndex={
                                                                        activeTab !==
                                                                        index
                                                                            ? -1
                                                                            : undefined
                                                                    }
                                                                    appearance="Purple"
                                                                    style="Outline"
                                                                >
                                                                    {
                                                                        tab
                                                                            .content
                                                                            .secondaryCta
                                                                            .content
                                                                            .text
                                                                    }
                                                                </LinkButton>
                                                            )}
                                                        </SecondaryCtaContainer>
                                                    </>
                                                )}
                                            </Secondary>
                                        )}
                                        <AnalyticsAttributesProvider
                                            value={{
                                                eventComponent:
                                                    "globalNavSecondaryButton"
                                            }}
                                        >
                                            {tab.content.secondaryBanners
                                                ?.filter(
                                                    ({ type }) =>
                                                        type === "uiNavBanner"
                                                )
                                                .map((banner, bannerIndex) => (
                                                    <Secondary
                                                        key={bannerIndex}
                                                    >
                                                        <UiNavBanner
                                                            model={banner}
                                                            tabIndex={
                                                                activeTab !==
                                                                index
                                                                    ? -1
                                                                    : undefined
                                                            }
                                                        />
                                                    </Secondary>
                                                ))}
                                        </AnalyticsAttributesProvider>
                                    </AnalyticsAttributesProvider>
                                </Page>
                            ) : null
                        )}
                    </PageContainer>
                </Header>
            </FocusTrap>
        </AnalyticsAttributesProvider>
    );
};

export default SmallNav;
